import {
  Asset,
  AssetRepository,
  AssetSummary,
  AssetType,
  DownloadItem,
  ExternalAssetsResponse,
  GetAssetByIdOrSlugParams,
  GetAssetsParams,
  GetAssetsResponse,
} from '@/lib/assets/types'
import {
  Category,
  Content,
  getCategoryByIdOrSlug,
  getContentByIdOrSlug,
  getContents,
  getMainCategoryByIdOrSlug,
  getSubCategoryByIdOrSlug,
  MainCategory,
  SubCategory,
} from '@/lib/categories/categories'
import {
  defaultBrowseBroadnessFilterValue,
  defaultBrowseSortFilterValue,
  defaultSearchBroadnessFilterValue,
  defaultSearchSortFilterValue,
} from '@/lib/filters/filters'
import { LEGACY_API_BASE_URL, MULTI_CONTENT_ASSETS_LIMIT } from '@/lib/config'
import { MEMBERS_API_BASE_URL } from '@/lib/api/members-api-client'

export class LegacyApiAssetRepository implements AssetRepository {
  async getAssets({
    mainCategorySlug,
    categorySlug,
    subCategorySlug,
    contentSlug,
    page,
    search,
    premium,
    sort,
    resolution,
    broadness,
    contentsLimit,
  }: GetAssetsParams): Promise<GetAssetsResponse> {
    let content: Content | null = null

    if (contentSlug) {
      content = await getContentByIdOrSlug(contentSlug, {
        mainCategoryIdOrSlug: mainCategorySlug,
        categoryIdOrSlug: categorySlug,
        subCategoryIdOrSlug: subCategorySlug,
      })
    }

    let contents: Content[] = content
      ? [content]
      : await getContents({
          mainCategoryIdOrSlug: mainCategorySlug,
          subCategoryIdOrSlug: subCategorySlug,
          categoryIdOrSlug: categorySlug,
        })

    console.debug(
      'Getting external assets for',
      'Main category:',
      mainCategorySlug,
      'Category:',
      subCategorySlug,
      'SubCategory:',
      categorySlug,
      'Contents',
      contents.map((c) => c.slug).join(', '),
      'Page:',
      page,
      'Search',
      search,
      'Broadness:',
      broadness,
      'Premium:',
      premium,
      'Sort:',
      sort,
      'Resolution:',
      resolution,
      'Contents limit:',
      contentsLimit
    )

    if (contentsLimit) {
      console.debug('Limiting contents to', contentsLimit)
      contents = contents.slice(0, contentsLimit)
    }

    // Store the urls already fetched, so we don't fetch them again
    const fetchCache = new Set<string>()

    // Parallel calls to get assets for each content
    const externalAssetsCalls: Promise<Partial<AssetSummary>[]>[] = contents.map(async (content) => {
      const mainCategory = await getMainCategoryByIdOrSlug(content.mainCategorySlug)

      if (!mainCategory) {
        console.warn('Main category not found for content', content.slug)
        return []
      }

      const category = await getCategoryByIdOrSlug(content.categorySlug, {
        mainCategoryIdOrSlug: mainCategory.slug,
      })

      if (!category) {
        console.warn('Category not found for content', content.slug)
        return []
      }

      const subCategory = await getSubCategoryByIdOrSlug(content.subCategorySlug, {
        mainCategoryIdOrSlug: mainCategory.slug,
        categoryIdOrSlug: category.slug,
      })

      if (!subCategory) {
        console.warn('SubCategory not found for content', content.slug)
        return []
      }

      // If search is defined, use the 'query' search type, otherwise use the content search type
      const contentSearchType = content.searchTag ? 'tag' : 'query'
      const searchType = search ? 'query' : contentSearchType

      const query = search ?? content.searchTag ?? content.searchQuery ?? content.slug

      // If we have a search query, use the default search broadness value,
      // else use the content search broadness value or the default browse broadness value
      let resolvedBroadness = !!search
        ? (broadness ?? defaultSearchBroadnessFilterValue)
        : (broadness ?? content.searchBroadness ?? defaultBrowseBroadnessFilterValue)

      // If content defines a min search broadness, use it
      if (content.minSearchBroadness && resolvedBroadness < content.minSearchBroadness) {
        console.debug('Using min search broadness for content', content.slug, content.minSearchBroadness)
        resolvedBroadness = content.minSearchBroadness
      }

      const searchCategory = content.searchCategory

      // Determine the default sort value
      // If we don't have a search query, use the default browse sort value, else use the default search sort value
      const defaultSort = !search ? defaultBrowseSortFilterValue : defaultSearchSortFilterValue

      const sortQuery = `order_by/${sort ?? defaultSort}`
      const broadnessQuery = `broadness/${resolvedBroadness}`

      const baseDataURL = `${LEGACY_API_BASE_URL}/search-json/${mainCategory.searchPath}/v/2/${searchType}/${encodeURIComponent(
        query
      )}`

      // If the base data url is already fetched, return an empty array
      // Include the category asset type in the cache key, as it can change between categories
      const fetchCacheKey = `${baseDataURL}/category_asset_type/${category.assetType}`

      if (fetchCache.has(fetchCacheKey)) {
        // console.debug('Already fetched for key', fetchCacheKey)
        return []
      }

      fetchCache.add(fetchCacheKey)

      const dataUrl =
        `${baseDataURL}/${sortQuery}/${broadnessQuery}` +
        (searchCategory ? `/category/${searchCategory}` : '') +
        (mainCategory.searchPath === 'rc' ? '/rt/all_3d' : '')

      console.debug('Getting for assets from', dataUrl)
      console.debug('> Fetch cache key', fetchCacheKey)

      const response = await fetch(`${dataUrl}`, {
        headers: {
          'Content-Type': 'application/json',
        },
        next: {
          revalidate: 3600,
        },
      })
      const jsonResponse = await response.json()
      const externalAssetsResponse = jsonResponse as ExternalAssetsResponse

      return this.extractExternalAssets(category, externalAssetsResponse).map((externalAsset) => {
        return {
          ...externalAsset,
          mainCategorySlug: mainCategory.slug,
          categorySlug: category.slug,
          subCategorySlug: subCategory.slug,
          contentSlug: content.slug,
        }
      })
    })

    let responses = await Promise.all(externalAssetsCalls)

    const totalAssets = responses.reduce((acc, response) => acc + response.length, 0)
    console.debug('Total assets:', totalAssets)

    // If we're searching multiple contents, limit the results to 100 assets per content
    const limit = contents.length > 1 ? MULTI_CONTENT_ASSETS_LIMIT : undefined

    if (limit) {
      console.debug('Limiting assets to', limit, 'for each content')
      responses = responses.map((response) => response.slice(0, limit))
    }

    let filteredAssets = responses
      .flatMap((response) => response)
      // Apply filters
      .filter((asset) => {
        if (premium !== undefined) {
          return asset.level && +asset.level === (premium ? 1 : 0)
        }

        return true
      })
      .filter((asset) => {
        if (resolution !== undefined && 'dimensions' in asset && asset.dimensions) {
          const [width] = asset.dimensions.split('x').map(Number)

          if (resolution === '4k') {
            return width >= 3840
          }

          if (resolution === '-4k') {
            return width < 3840
          }
        }

        return true
      })

    let assets: AssetSummary[] = []

    for (const externalAsset of filteredAssets) {
      if (!externalAsset.mainCategorySlug) {
        console.warn('Main category not found for asset', externalAsset)
        continue
      }

      const mainCategory = await getMainCategoryByIdOrSlug(externalAsset.mainCategorySlug)

      if (!mainCategory) {
        console.warn('Main category not found for asset', externalAsset)
        continue
      }

      if (!externalAsset.categorySlug) {
        console.warn('Category not found for asset', externalAsset)
        continue
      }

      const category = await getCategoryByIdOrSlug(externalAsset.categorySlug, {
        mainCategoryIdOrSlug: mainCategory.id,
      })

      if (!category) {
        console.warn('Category not found for asset', externalAsset)
        continue
      }

      let subCategory: SubCategory | null = null

      if (externalAsset.subCategorySlug) {
        subCategory = await getSubCategoryByIdOrSlug(externalAsset.subCategorySlug, {
          mainCategoryIdOrSlug: mainCategory.id,
          categoryIdOrSlug: category.id,
        })
      }

      assets.push(
        this.parseExternalAsset<AssetSummary>({
          type: category.assetType,
          mainCategory,
          category,
          subCategory,
          content,
          externalAsset,
        })
      )
    }

    return {
      data: assets,
      meta: undefined, // Setting meta to undefined will force local pagination on the client side
    }
  }

  async getAssetByIdOrSlug({
    assetIdOrSlug,
    type,
    mainCategoryIdOrSlug,
    categoryIdOrSlug,
    subCategoryIdOrSlug,
    contentIdOrSlug,
  }: GetAssetByIdOrSlugParams): Promise<Asset | null> {
    // Resolve and load assets
    const mainCategory = await getMainCategoryByIdOrSlug(mainCategoryIdOrSlug)

    if (!mainCategory) {
      throw new Error('Invalid params')
    }

    const category = categoryIdOrSlug
      ? await getCategoryByIdOrSlug(categoryIdOrSlug, {
          mainCategoryIdOrSlug: mainCategory.slug,
        })
      : null
    const subCategory = subCategoryIdOrSlug
      ? await getSubCategoryByIdOrSlug(subCategoryIdOrSlug, {
          mainCategoryIdOrSlug: mainCategory.slug,
          categoryIdOrSlug: category?.slug,
        })
      : null
    const content = contentIdOrSlug
      ? await getContentByIdOrSlug(contentIdOrSlug, {
          mainCategoryIdOrSlug: mainCategory.slug,
          categoryIdOrSlug: category?.slug,
          subCategoryIdOrSlug: subCategory?.slug,
        })
      : null

    const dataUrl = `${MEMBERS_API_BASE_URL}/content_data/${type}/${assetIdOrSlug}.json`

    console.debug('Getting asset from', dataUrl)

    const response = await fetch(`${dataUrl}`, {
      // TODO: Activate when server is ready to support Access-Control-Allow-Headers
      // headers: {
      //   'Content-Type': 'application/json',
      // },
      next: {
        revalidate: 3600,
      },
    })

    let externalAsset: Partial<Asset> = (await response.json()) as Partial<Asset>

    // Sometimes dl_opts is an object with keys as the item id,
    // so we need to convert it to an array.
    if (externalAsset.dl_opts && !Array.isArray(externalAsset.dl_opts.items)) {
      externalAsset.dl_opts.items = Object.entries(externalAsset.dl_opts.items).map(
        ([_, value]) => value
      ) as DownloadItem[]
    }

    // Handle additional data for fc_effects
    if (type === 'fc_effects') {
      const additionalDataUrl = `${LEGACY_API_BASE_URL}/data/video/${assetIdOrSlug}.json`

      console.debug('Getting additional data from', additionalDataUrl)

      const response = await fetch(`${additionalDataUrl}`, {
        // TODO: Activate when server is ready to support Access-Control-Allow-Headers
        // headers: {
        //   'Content-Type': 'application/json',
        // },
        next: {
          revalidate: 3600,
        },
      })

      const additionalData = (await response.json()) as Partial<Asset>

      externalAsset = { ...externalAsset, ...additionalData }
    }

    return this.parseExternalAsset<Asset>({
      type,
      mainCategory,
      category,
      subCategory,
      content,
      externalAsset,
    })
  }

  /**
   * Extract external assets from the response.
   *
   * @private
   * @param category
   * @param externalAssetsResponse
   */
  private extractExternalAssets(
    category: Category,
    externalAssetsResponse: ExternalAssetsResponse
  ): Partial<AssetSummary[]> {
    // For Render Crate, we need to check the type of asset
    if (externalAssetsResponse.search_type === 'rc') {
      if (category.assetType === 'rc_objects' && externalAssetsResponse.objects) {
        return externalAssetsResponse.objects ?? externalAssetsResponse.results ?? []
      }
      if (category.assetType === 'rc_materials' && externalAssetsResponse.materials) {
        return externalAssetsResponse.materials ?? externalAssetsResponse.results ?? []
      }
      if (category.assetType === 'rc_environments' && externalAssetsResponse.environments) {
        return externalAssetsResponse.environments ?? externalAssetsResponse.results ?? []
      }
    }

    // Append categorization and return
    return (externalAssetsResponse.results ?? []) as AssetSummary[]
  }

  /**
   * Parse an external asset.
   * This function is used to parse the external asset data.
   * It adds the main category, category, subCategory, and content data to the asset.
   *
   * @private
   * @param type
   * @param mainCategory
   * @param category
   * @param subCategory
   * @param content
   * @param externalAsset
   */
  private parseExternalAsset<T = Asset | AssetSummary>({
    type,
    mainCategory,
    category,
    subCategory,
    content,
    externalAsset,
  }: {
    type: AssetType
    mainCategory: MainCategory
    category?: Category | null
    subCategory?: SubCategory | null
    content?: Content | null
    externalAsset: Partial<Asset>
  }): T {
    return {
      __typename: type,
      ...externalAsset,
      level: 'level' in externalAsset ? +(externalAsset.level ?? 0) : 0,
      mainCategorySlug: mainCategory.slug,
      categorySlug: category?.slug,
      subCategorySlug: subCategory?.slug,
      contentSlug: content?.slug,
    } as T
  }
}

// TODO: Implement the keyword filtering and local pagination
/**
 * Count the number of keyword matches in the asset
 */
// const countMatches = useCallback(
//   (asset: AssetSummary) => {
//     return usedKeywords.reduce((acc, keyword) => {
//       return (
//         acc +
//         `${asset.title.toLowerCase()} ${asset.keywords ?? ''}`
//           .split(' ')
//           .filter(Boolean)
//           .reduce((acc, word) => {
//             return acc + (word.includes(keyword) ? 1 : 0)
//           }, 0)
//       )
//     }, 0)
//   },
//   [usedKeywords]
// )

// Extract the assets, paginate and filter data from the asset response.
// Also filter assets by searchQuery box value and selected keywords.
// Finally, sort the assets by the best result, based on matches count.
// const filteredAssets = useMemo(() => {
//   return (assetsResponse?.data ?? [])
//     .filter((asset) => {
//       return !usedKeywords.length || countMatches(asset) > 0
//     })
//     .sort((a, b) => countMatches(b) - countMatches(a))
// }, [assetsResponse?.data, countMatches, usedKeywords.length])

// The displayed assets are the paginated filtered assets
// const assets = useMemo(() => {
//   const startIndex = initialPage ? (initialPage - 1) * ITEMS_PER_PAGE : 0
//   const endIndex = ITEMS_PER_PAGE * page
//
//   return filteredAssets.slice(startIndex, endIndex)
// }, [filteredAssets, initialPage, page])

// Get the assets keywords map
// const isLocalTags = useMemo(() => {
//   return !assetsResponse?.meta
// }, [assetsResponse?.meta])

// const assetsKeywords = useMemo(() => {
//   if (!isLocalTags) {
//     return assetsResponse?.meta?.tags ?? []
//   }
//
//   const splitKeywords = (assetsResponse?.data ?? [])
//     .reduce((acc, asset) => {
//       const keywords = [...asset.title.toLowerCase().split(' '), ...(asset.keywords?.toLowerCase().split(' ') ?? [])]
//       return [...acc, ...keywords]
//     }, [] as string[])
//     // Keep only alphabetical keywords
//     .filter((keyword) => !keyword.match(/[^a-zA-Z]/))
//     // Remove empty keywords
//     .filter(Boolean)
//     // Remove common connectors
//     .filter((keyword) => !commonConnectors.includes(keyword))
//
//   // Pick the most popular keywords by sort the split keywords by number of occurrences
//   const sortedKeywords = splitKeywords.sort(
//     (a, b) =>
//       splitKeywords.filter((keyword) => keyword === b).length -
//       splitKeywords.filter((keyword) => keyword === a).length
//   )
//
//   // Finally, remove duplicates and pick the first 15 keywords
//   return sortedKeywords
//     .filter((keyword, index) => sortedKeywords.indexOf(keyword) === index)
//     .slice(0, MAX_ASSETS_FILTERS_TAGS)
// }, [assetsResponse?.data, assetsResponse?.meta?.tags, isLocalTags])

// Pagination
// const isLocalPagination = useMemo(() => {
//   return !assetsResponse?.meta
// }, [assetsResponse?.meta])

// const totalAssets = isLocalPagination ? assetsResponse?.data?.length : assetsResponse!.meta!.total_items
// const totalPages = isLocalPagination ? Math.ceil(totalAssets! / ITEMS_PER_PAGE) : assetsResponse!.meta!.total_pages

// const { hasMore, loadMore } = useHandleInfinitePagination({
//   page,
//   setPage,
//   totalPages,
// })

// useEffect(() => {
//   console.debug('Showing page %d of %d', page, totalPages)
// }, [page, totalPages, hasMore])

// Group assets and paginate
// const groupedAssets: GroupedAssets[] = useMemo(() => {
//   return (
//     (assets ?? [])
//     .reduce((acc, asset) => {
//       const mainCategory = mainCategories?.find((mc) => mc.slug === asset.mainCategorySlug)
//
//       if (!!mainCategory) {
//         const item = acc.find((group) => group.mainCategory.slug === asset.mainCategorySlug)
//
//         if (item) {
//           item.assets.push(asset)
//         } else {
//           acc.push({
//             mainCategory,
//             assets: [asset],
//           })
//         }
//       }
//
//       return acc
//     }, [] as GroupedAssets[])
//       // Paginate the results.
//       // When is a multi-category searchQuery, show only the first page per category.
//       .map((group) => ({
//         ...group,
//         assets: group.assets.slice(0, isMultiCategorySearch ? ITEMS_PER_PAGE : ITEMS_PER_PAGE * page),
//       }))
//       .sort((a, b) => {
//       return (
//         (mainCategories?.findIndex((mc) => mc.slug === a.mainCategory.slug) ?? 0) -
//         (mainCategories?.findIndex((mc) => mc.slug === b.mainCategory.slug) ?? 0)
//       )
//     })
//   )
// }, [assets, isMultiCategorySearch, mainCategories, page])

// Pagination
// const totalAssets = assets?.length ?? 0
// const totalPages = Math.ceil(totalAssets / ITEMS_PER_PAGE)
